import { Component, OnDestroy, OnInit, input, output, signal } from '@angular/core';
import { Filter, FilterField } from '../filtered-search/filtered-search.component';
import { ApiConfig } from '../api-config/api-config';
import { BehaviorSubject, Observable, catchError, of, Subject, takeUntil, tap, finalize } from 'rxjs';
import { LogHelper } from '../logging/log-helper';
import { LazyCache } from '../cache/cache';
import { UsersService } from 'src/app/handweave-api';
import { FieldConfigurationService } from './field-configuration.service';
import { TimeSpan } from '../model/date.helper';

type FieldNode = {
  name: string;
  fullName: string;
  children?: FieldNode[];
};

@Component({
  selector: 'app-advanced-filter-selection',
  templateUrl: './advanced-filter-selection.component.html',
  styleUrl: './advanced-filter-selection.component.scss',
  standalone: false
})
export class AdvancedFilterSelectionComponent implements OnInit, OnDestroy {
  tenantId = input<string>(ApiConfig.tenantId);
  defaults = input<Filter[]>();
  fieldTransformer = input<BehaviorSubject<(filterFields: FilterField[]) => FilterField[]>>();
  filtersChange = output<Filter[]>();

  error = signal<string | null>(null);
  loading = signal(false);
  filterFields = signal<FilterField[]>([]);
  availableFields: FieldNode[] = [];
  destroy$ = new Subject<void>();

  constructor(
    private readonly usersService: UsersService,
    private readonly fieldConfigurationService: FieldConfigurationService,
  ) {}

  ngOnInit(): void {
    this.fieldConfigurationService.register();
    this.fieldTransformer()
      ?.pipe(takeUntil(this.destroy$))
      .subscribe((fieldFn) => {
        if (!fieldFn) return;

        this.filterFields?.update((f) => {
          if (!f) return [];

          return fieldFn(f);
        });
      });

    this.getAvailableFields();
  }

  ngOnDestroy(): void {
    this.fieldConfigurationService.unregister();
    this.searchCache.clear();
    this.destroy$.next();
    this.destroy$.complete();
  }

  getAvailableFields() {
    const type = 'usersv2';

    this.error.set(null);
    this.loading.set(true);
    this.fieldConfigurationService
      .getFieldConfiguration(this.tenantId(), type)
      .pipe(
        tap(({ fieldConfigurations, displayNames }) => {
          this.availableFields = this.buildFieldNodes(fieldConfigurations);
          this.filterFields.set(this.populateFilterFields(this.availableFields, displayNames));
          this.filterFields.set(this.fieldTransformer()?.value?.(this.filterFields()) ?? this.filterFields());
        }),
        catchError((error) => {
          LogHelper.debug('AdvancedFilterSelection', 'Error fetching field configurations:', error);
          this.error.set('Error fetching available fields. Try again?');
          return of(null);
        }),
        finalize(() => this.loading.set(false)),
      )
      .subscribe();
  }

  private populateFilterFields(fields: FieldNode[], displayNames: Record<string, string>): FilterField[] {
    if (!fields) return [];
    return fields.flatMap((field) => {
      const children = this.populateFilterFields(field.children, displayNames);
      return children?.length ? children : [this.createFilterField(field, displayNames)];
    });
  }

  private createFilterField(field: FieldNode, displayNames?: Record<string, string>): FilterField {
    return {
      field: field.fullName,
      localizedLabel: displayNames?.[field.fullName] ?? field.fullName,
      hide: false,
      type: 'select',
      valuesFunc: (field: string) => this.fieldSearch(field),
    } as FilterField;
  }

  private readonly searchCache = new LazyCache<string[]>();
  private fieldSearch(field: string): (input: string) => Observable<string[]> {
    return (input: string) => {
      const query = input.trim() + '*';
      const cacheKey = `${field}:${query}`;
      const cached = this.searchCache.get(cacheKey);

      if (cached) {
        return of(cached);
      }

      return this.searchCache.fetchWithCache(
        cacheKey,
        this.usersService
          .usersGetPossibleValuesByPage({
            tenantId: this.tenantId(),
            page: 1,
            pageSize: 50,
            field,
            query,
          })
          .pipe(
            catchError((error) => {
              LogHelper.debug('AlertRecipientsV2Search', 'Error fetching field values:', error);
              return of([]);
            }),
          ),
        { expirationMs: TimeSpan.fromMinutes(10) },
      );
    };
  }

  private buildFieldNodes(fields: string[], parent?: string): FieldNode[] {
    const nodes = fields.map((field) => field.split('.'));
    const groupedByRoot: Record<string, string[][]> = {};

    for (const path of nodes) {
      if (path.length > 0) {
        const rootName = path[0];
        if (!groupedByRoot[rootName]) {
          groupedByRoot[rootName] = [];
        }
        groupedByRoot[rootName].push(path.slice(1));
      }
    }

    return Object.entries(groupedByRoot).map(([rootName, subpaths]) => {
      const fullName = parent ? `${parent}.${rootName}` : rootName;
      const node: FieldNode = { name: rootName, fullName: fullName };

      const nonEmptySubpaths = subpaths.filter((path) => path.length > 0);

      if (nonEmptySubpaths.length > 0) {
        node.children = this.buildFieldNodesRecursive(nonEmptySubpaths, fullName);
      }

      return node;
    });
  }

  private buildFieldNodesRecursive(subpaths: string[][], root: string): FieldNode[] {
    const subFields = subpaths.map((path) => path.join('.'));

    const validSubFields = subFields.filter((field) => field.length > 0);

    if (validSubFields.length === 0) {
      return [];
    }

    return this.buildFieldNodes(validSubFields, root);
  }
}
